// eslint-disable-next-line import/named
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../../store/store'

interface OrganizationDetails {
  customerName: string
  workorderSystem: string
  facilitySystem: string
  apiUrl: string
  integration_id?: any
  integration_type?: string
  org_id?: number
}

interface FieldData {
  fieldName: string
  mandatory: string
  dataType: string
  requestLocation: string
  defaultValue: string
  source_object_field_name: string
  mergeLineitems?: boolean
  match_values: {
    match_value_type: string
    values_mapping: {
      source_field_name: string
      converted_value: string
    }[]
  }
}

interface FileRenameConfig {
  staticValue?: string
  selectedFields?: string[]
  preview?: string
  field_mapping_id?: string
  value_mapping_id?: string
}

interface MappingConfig {
  endpoint: string
  methodType: string
  deciderField: string
  deciderValue: string[]
  objectType: string
  fields: FieldData[]
  selectedValuesArrays?: string[]
  apiHeader: string
  multi_decider_fields_name?: string[]
  multi_decider_fields_value?: string[]
  needsDelay?: boolean
  delay?: number
  needsEscalation?: boolean
  configure_status?: boolean
  escalationEmail?: string | null
  escalationEmailError?: string | null
  enable_email?: boolean
  email_address?: string
  file_enabled_statuses?: string[]
  fileRenameConfig?: FileRenameConfig
  isFinalizedValue?: string
  isEscalationValue?: string
}

interface MappingData extends MappingConfig {
  fields: FieldData[]
}

interface CafmEndpointData {
  customerName?: string
  workorderSystem?: string
  facilitySystem?: string
  apiUrl?: string
  endpoint?: string
  methodType?: string
  deciderField?: string
  deciderValues?: string[]
  multi_decider_fields_name?: string[] // optional extra fields
  multi_decider_fields_value?: string[] // optional extra values
}

interface CafmState {
  mappings: any[]
  id: number | null
  selectedMapping: any | null
  organizationDetails: OrganizationDetails | null
  currentMappingConfig: MappingConfig
  fieldMappings: MappingData[]
  masterData: any
  deciderFieldValues: string[]
  cafmEndpointStore: CafmEndpointData
  loading: boolean
  error: string | null
}

const initialState: CafmState = {
  mappings: [],
  id: null,
  selectedMapping: null,
  organizationDetails: null,
  currentMappingConfig: {
    endpoint: '',
    methodType: 'GET',
    deciderField: '', // 🔧 FIX: Empty string to prevent unwanted fields from showing on initial load
    deciderValue: [], // 🔧 FIX: Empty array
    objectType: '', // 🔧 FIX: Empty string to prevent default object type
    apiHeader: '',
    fields: [],
    selectedValuesArrays: [], // 🔧 FIX: Empty array to prevent default values
    multi_decider_fields_name: [],
    multi_decider_fields_value: [],
  },
  fieldMappings: [],
  masterData: null,
  deciderFieldValues: [],
  cafmEndpointStore: {},
  loading: false,
  error: null,
}

const cafmSlice = createSlice({
  name: 'cafm',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    setMappings: (state, action) => {
      state.mappings = action.payload
    },
    setSelectedMapping: (state, action) => {
      state.selectedMapping = action.payload
    },
    clearMappingsOnOrgSwitch: (state) => {
      state.mappings = []
      state.selectedMapping = null
      state.loading = true
    },
    setId: (state, action) => {
      state.id = action.payload
    },
    setOrganizationDetails: (state, action) => {
      state.organizationDetails = action.payload
    },
    addFieldData: (state, action: PayloadAction<FieldData>) => {
      const existingFieldIndex = state.currentMappingConfig.fields.findIndex(
        (field) => field.fieldName === action.payload.fieldName,
      )
      if (existingFieldIndex === -1) {
        state.currentMappingConfig.fields.push(action.payload)
      } else {
        state.currentMappingConfig.fields[existingFieldIndex] = action.payload
      }
    },
    updateFieldData: (state, action: PayloadAction<FieldData>) => {
      const index = state.currentMappingConfig.fields.findIndex((field) => field.fieldName === action.payload.fieldName)
      if (index !== -1) {
        state.currentMappingConfig.fields[index] = action.payload
      } else {
        state.currentMappingConfig.fields.push(action.payload)
      }
    },
    setFieldMappings: (state, action) => {
      state.fieldMappings = action.payload
    },
    clearFieldMappings: (state) => {
      state.fieldMappings = []
    },
    setCurrentMappingConfig: (state, action: PayloadAction<MappingConfig>) => {
      state.currentMappingConfig = action.payload
    },
    // Merge partial config updates with current state (prevents race conditions)
    updateCurrentMappingConfig: (state, action: PayloadAction<Partial<MappingConfig>>) => {
      state.currentMappingConfig = { ...state.currentMappingConfig, ...action.payload }
    },
    setCurrentEndpoint: (state, action: PayloadAction<string>) => {
      state.currentMappingConfig.endpoint = action.payload
    },
    setCurrentMethodType: (state, action: PayloadAction<string>) => {
      state.currentMappingConfig.methodType = action.payload
    },
    setCurrentDeciderField: (state, action: PayloadAction<string>) => {
      state.currentMappingConfig.deciderField = action.payload
    },
    setCurrentDeciderValue: (state, action: PayloadAction<string[]>) => {
      state.currentMappingConfig.deciderValue = action.payload
    },
    setCurrentObjectType: (state, action: PayloadAction<string>) => {
      state.currentMappingConfig.objectType = action.payload
    },
    setMasterData: (state, action) => {
      state.masterData = action.payload
    },
    setDeciderFieldValues: (state, action) => {
      state.deciderFieldValues = action.payload
    },
    setMultiDeciderFieldsName: (state, action) => {
      state.currentMappingConfig.multi_decider_fields_name = action.payload
    },

    setMultiDeciderFieldsValue: (state, action) => {
      state.currentMappingConfig.multi_decider_fields_value = action.payload
    },
    setCafmEndpointStore: (state, action: PayloadAction<CafmEndpointData>) => {
      state.cafmEndpointStore = action.payload
    },
    setCurrentDeciderValues: (state, action: PayloadAction<string[]>) => {
      state.currentMappingConfig.selectedValuesArrays = action.payload
    },
    addFieldMapping: (state, action: PayloadAction<MappingData>) => {
      const existingMappingIndex = state.fieldMappings.findIndex(
        (mapping) =>
          mapping.endpoint === action.payload.endpoint &&
          mapping.methodType === action.payload.methodType &&
          mapping.objectType === action.payload.objectType,
      )
      if (existingMappingIndex !== -1) {
        const existingFields = state.fieldMappings[existingMappingIndex].fields
        const newFields = action.payload.fields.filter(
          (newField) => !existingFields.some((field) => field.fieldName === newField.fieldName),
        )
        state.fieldMappings[existingMappingIndex].fields.push(...newFields)
      } else {
        state.fieldMappings.push(action.payload)
      }
    },
    updateFieldMapping: (state, action: PayloadAction<MappingData>) => {
      const index = state.fieldMappings.findIndex(
        (mapping) =>
          mapping.endpoint === action.payload.endpoint &&
          mapping.methodType === action.payload.methodType &&
          mapping.objectType === action.payload.objectType,
      )
      if (index !== -1) {
        const existingFields = state.fieldMappings[index].fields
        const updatedFields = action.payload.fields.map((newField) => {
          // Use both fieldName and source_object_field_name for uniqueness
          const existingFieldIndex = existingFields.findIndex(
            (field) =>
              field.fieldName === newField.fieldName &&
              field.source_object_field_name === newField.source_object_field_name,
          )
          if (existingFieldIndex !== -1) {
            return { ...existingFields[existingFieldIndex], ...newField }
          }
          return newField
        })
        state.fieldMappings[index].fields = updatedFields
      }
    },
    updateFieldInMapping: (state, action: PayloadAction<{ mappingId: string; field: FieldData; originalFieldName?: string }>) => {
      const { mappingId, field, originalFieldName } = action.payload
      const mappingIndex = state.fieldMappings.findIndex((mapping) => mapping.endpoint === mappingId)
      if (mappingIndex !== -1) {
        // Use originalFieldName (pre-rename) when provided so renames are found correctly;
        // fall back to field.fieldName for non-rename updates
        const lookupName = originalFieldName || field.fieldName
        const fieldIndex = state.fieldMappings[mappingIndex].fields.findIndex((f) => f.fieldName === lookupName)
        if (fieldIndex !== -1) {
          state.fieldMappings[mappingIndex].fields[fieldIndex] = {
            ...state.fieldMappings[mappingIndex].fields[fieldIndex],
            ...field,
          }
        } else {
          state.fieldMappings[mappingIndex].fields.push(field)
        }
      }
    },
    updateMatchField: (
      state,
      action: PayloadAction<{ mappingId: string; fieldName: string; matchField: string }>, // Changed from string | string[] to string
    ) => {
      const { mappingId, fieldName, matchField } = action.payload
      const mappingIndex = state.fieldMappings.findIndex((mapping) => mapping.endpoint === mappingId)
      if (mappingIndex !== -1) {
        const fieldIndex = state.fieldMappings[mappingIndex].fields.findIndex((f) => f.fieldName === fieldName)
        if (fieldIndex !== -1) {
          state.fieldMappings[mappingIndex].fields[fieldIndex].source_object_field_name = matchField
        }
      }
    },
    updateMatchValue: (state, action: PayloadAction<{ mappingId: string; fieldName: string; matchValue: string }>) => {
      const { mappingId, fieldName, matchValue } = action.payload
      const mappingIndex = state.fieldMappings.findIndex((mapping) => mapping.endpoint === mappingId)
      if (mappingIndex !== -1) {
        const fieldIndex = state.fieldMappings[mappingIndex].fields.findIndex((f) => f.fieldName === fieldName)
        if (fieldIndex !== -1) {
          state.fieldMappings[mappingIndex].fields[fieldIndex].defaultValue = matchValue
        }
      }
    },
    deleteFieldFromMapping: (state, action: PayloadAction<{ mappingId: string; fieldName: string }>) => {
      const { mappingId, fieldName } = action.payload
      const mappingIndex = state.fieldMappings.findIndex((mapping) => mapping.endpoint === mappingId)
      if (mappingIndex !== -1) {
        state.fieldMappings[mappingIndex].fields = state.fieldMappings[mappingIndex].fields.filter(
          (field) => field.fieldName !== fieldName,
        )
      }
    },
    updateSelectedMappingAuthType: (state, action) => {
      state.selectedMapping.auth_type = action.payload
    },
    setAPIHeader: (state, action: PayloadAction<string>) => {
      state.currentMappingConfig.apiHeader = action.payload
    },
    resetState: (state) => {
      state.currentMappingConfig = initialState.currentMappingConfig
      state.fieldMappings = initialState.fieldMappings
      state.deciderFieldValues = initialState.deciderFieldValues
      state.cafmEndpointStore = initialState.cafmEndpointStore
    },
  },
})

export const {
  setLoading,
  setError,
  setMappings,
  setSelectedMapping,
  clearMappingsOnOrgSwitch,
  setId,
  setOrganizationDetails,
  addFieldData,
  updateFieldData,
  setFieldMappings,
  clearFieldMappings,
  setCurrentMappingConfig,
  updateCurrentMappingConfig,
  setCurrentEndpoint,
  setCurrentMethodType,
  setCurrentDeciderField,
  setCurrentDeciderValue,
  setCurrentObjectType,
  setMasterData,
  setDeciderFieldValues,
  setCafmEndpointStore,
  setCurrentDeciderValues,
  addFieldMapping,
  updateFieldMapping,
  updateFieldInMapping,
  updateMatchField,
  updateMatchValue,
  deleteFieldFromMapping,
  updateSelectedMappingAuthType,
  setAPIHeader,
  resetState,
  setMultiDeciderFieldsName,
  setMultiDeciderFieldsValue,
} = cafmSlice.actions

export const selectMappingId = (state: RootState) => state.cafm.id
export const selectOrganizationDetails = (state: RootState) => state.cafm.organizationDetails
export const selectFieldMappings = (state: RootState) => state.cafm.fieldMappings
export const selectCurrentMappingConfig = (state: RootState) => state.cafm.currentMappingConfig
export const selectMasterData = (state: RootState) => state.cafm.masterData
export const selectDeciderFieldValues = (state: RootState) => state.cafm.deciderFieldValues
export const selectCafmEndpointStore = (state: RootState) => state.cafm.cafmEndpointStore

export default cafmSlice.reducer
